let socket = null;
let token = null;
let openPromise = null;
let openResolve = null;
let reconnectTimeout = null;
let retryCount = 0;
let isActive = true;

const pending = new Map();
const subscribers = new Set();

function buildWebSocketUrl() {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    if (process.env.REACT_APP_WS_URL) {
        return process.env.REACT_APP_WS_URL;
    }
    return `${protocol}://${window.location.host}/ws-api`;
}

function notifyEvent(message) {
    subscribers.forEach((handler) => {
        handler(message);
    });
}

function rejectAllPending(error) {
    pending.forEach(({ reject }) => {
        reject(error);
    });
    pending.clear();
}

function connect() {
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        return;
    }

    if (!isActive) {
        return;
    }

    socket = new WebSocket(buildWebSocketUrl());
    openPromise = new Promise((resolve) => {
        openResolve = resolve;
    });

    socket.onopen = () => {
        retryCount = 0;
        if (openResolve) {
            openResolve();
        }
        if (token) {
            request("auth", { token }).catch(() => {});
        }
    };

    socket.onmessage = (event) => {
        try {
            const message = JSON.parse(event.data);
            if (message.id && pending.has(message.id)) {
                const { resolve, reject } = pending.get(message.id);
                pending.delete(message.id);
                if (message.ok) {
                    resolve(message.data);
                } else {
                    reject(new Error(message.error || "Request failed"));
                }
                return;
            }
            if (message.type === "event") {
                notifyEvent(message);
                return;
            }
        } catch (err) {
            console.log("Invalid websocket message", err);
        }
    };

    socket.onclose = () => {
        rejectAllPending(new Error("Websocket disconnected"));
        if (!isActive) {
            return;
        }
        const delay = Math.min(10000, 1000 * (retryCount + 1));
        retryCount += 1;
        reconnectTimeout = setTimeout(connect, delay);
    };

    socket.onerror = () => {
        if (socket) {
            socket.close();
        }
    };
}

async function ensureOpen() {
    connect();
    if (openPromise) {
        await openPromise;
    }
}

export async function request(action, payload = {}) {
    await ensureOpen();
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const message = { id, action, payload };
    if (token) {
        message.token = token;
    }
    return new Promise((resolve, reject) => {
        pending.set(id, { resolve, reject });
        socket.send(JSON.stringify(message));
    });
}

export function subscribe(handler) {
    subscribers.add(handler);
    return () => {
        subscribers.delete(handler);
    };
}

export function setToken(nextToken) {
    token = nextToken || null;
    if (!token && socket) {
        isActive = false;
        socket.close();
        socket = null;
        isActive = true;
        return;
    }
    connect();
    if (socket && socket.readyState === WebSocket.OPEN && token) {
        request("auth", { token }).catch(() => {});
    }
}
